import React from 'react';
import { getPowerTier, getTierProgress, getNextTier } from '../lib/powerLevel';
import { useLanguage } from '../context/LanguageContext';

export function PowerLevelBadge({ elo = 1200, size = 'md', showLabel = true }) {
  const { t } = useLanguage();
  const tier = getPowerTier(elo);
  const progress = getTierProgress(elo);
  const nextTier = getNextTier(elo);

  const sizes = { sm: 48, md: 80, lg: 120 };
  const s = sizes[size] || sizes.md;
  const strokeWidth = size === 'sm' ? 3 : size === 'lg' ? 6 : 4;
  const radius = (s / 2) - strokeWidth - 2;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference - (progress / 100) * circumference;

  return (
    <div className="flex flex-col items-center gap-1" data-testid="power-level-badge">
      <div className="relative" style={{ width: s, height: s }}>
        <svg width={s} height={s} className="transform -rotate-90">
          <circle cx={s/2} cy={s/2} r={radius} fill="none" stroke="currentColor" strokeWidth={strokeWidth} className="text-muted/30" />
          <circle
            cx={s/2} cy={s/2} r={radius} fill="none"
            stroke={tier.color} strokeWidth={strokeWidth}
            strokeDasharray={circumference} strokeDashoffset={dashOffset}
            strokeLinecap="round"
            className="transition-all duration-1000 ease-out"
            style={{ filter: `drop-shadow(0 0 4px ${tier.color}40)` }}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={`font-heading font-bold ${size === 'sm' ? 'text-xs' : size === 'lg' ? 'text-2xl' : 'text-base'}`} style={{ color: tier.color }}>
            {elo}
          </span>
        </div>
      </div>
      {showLabel && (
        <div className="text-center">
          <p className={`font-heading font-bold uppercase tracking-wider ${size === 'sm' ? 'text-[10px]' : 'text-xs'}`} style={{ color: tier.color }}>
            {t(`powerLevel.${tier.name}`)}
          </p>
          {nextTier && size !== 'sm' && (
            <p className="text-[10px] text-muted-foreground">
              {nextTier.min - elo} to {t(`powerLevel.${nextTier.name}`)}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export function PowerLevelInline({ elo = 1200 }) {
  const { t } = useLanguage();
  const tier = getPowerTier(elo);
  return (
    <span className="inline-flex items-center gap-1.5 font-heading font-bold text-sm" style={{ color: tier.color }} data-testid="power-level-inline">
      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: tier.color, boxShadow: `0 0 6px ${tier.color}` }} />
      {elo} {t(`powerLevel.${tier.name}`)}
    </span>
  );
}
